import { useCallback, useEffect, useRef } from 'react';
import { Keyboard } from 'react-native';

// Keep a native discovery visit separate from the temporary keyboard layout.
// The draft lives in the existing input store; these refs hold only scroll state.
export default function useHomeDiscoveryNavigation({ enabled, native, keyboardOpen, scrollRef, metricsRef, feedOffset, setMessage, focusInput, contextKey }) {
  const origin = useRef(0);
  const pendingReturn = useRef(null);
  const selecting = useRef(false);
  const wasOpen = useRef(keyboardOpen);
  const frame = useRef(null);
  const offset = useRef(feedOffset); offset.current = feedOffset;
  const cancelFrame = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
  }, []);
  const afterLayout = useCallback(callback => {
    cancelFrame();
    frame.current = requestAnimationFrame(() => { frame.current = null; callback(); });
  }, [cancelFrame]);
  useEffect(() => {
    cancelFrame();
    origin.current = 0;
    pendingReturn.current = null;
    selecting.current = false;
    wasOpen.current = keyboardOpen;
  }, [contextKey, cancelFrame]);
  useEffect(() => {
    if (!enabled || !native) { wasOpen.current = keyboardOpen; cancelFrame(); return undefined; }
    if (keyboardOpen && !wasOpen.current) {
      if (!selecting.current) origin.current = metricsRef.current.y || 0;
      selecting.current = false;
      scrollRef.current?.scrollTo({ y: 0, animated: false });
    } else if (!keyboardOpen && wasOpen.current) {
      const target = pendingReturn.current ?? origin.current;
      pendingReturn.current = null;
      afterLayout(() => scrollRef.current?.scrollTo({ y: target === 'feed' ? Math.max(offset.current, origin.current) : target, animated: false }));
    }
    wasOpen.current = keyboardOpen;
    return undefined;
  }, [enabled, native, keyboardOpen, metricsRef, scrollRef, afterLayout, cancelFrame]);
  useEffect(() => () => cancelFrame(), [cancelFrame]);
  const explore = useCallback(() => {
    if (!enabled) return;
    if (native && keyboardOpen) {
      pendingReturn.current = 'feed';
      Keyboard.dismiss();
    } else scrollRef.current?.scrollTo({ y: offset.current, animated: true });
  }, [enabled, native, keyboardOpen, scrollRef]);
  const done = useCallback(() => {
    if (!enabled) return;
    pendingReturn.current = origin.current;
    Keyboard.dismiss();
  }, [enabled]);
  const selectIdea = useCallback(prompt => {
    if (!enabled) return;
    setMessage(prompt);
    if (native) {
      origin.current = metricsRef.current.y || 0;
      selecting.current = true;
      scrollRef.current?.scrollTo({ y: 0, animated: false });
      afterLayout(focusInput);
    } else {
      scrollRef.current?.scrollTo({ y: 0, animated: true });
      focusInput();
    }
  }, [enabled, native, scrollRef, metricsRef, setMessage, focusInput, afterLayout]);
  return { explore, done, selectIdea };
}
